import type { PresetId } from '@manudev/pipeline/presets';
import type { MeshData, RvtElement } from '@manudev/rvt';
import * as THREE from 'three';
import { meshSource, type SourceElement, type SourceMesh } from '@/hero/sources';
import { buildBatches, elementBox, presetKey } from '@/render/batch';
import { rigLayer, type LayerRig } from '@/render/rig';

// the layer id selections and hover carry for Revit elements
export const RVT_LAYER = 'rvt';
// a box stands in for geometry the reader could not rebuild; it reads as a stand-in
const BOX_ALPHA = 0.3;
// the rig's horizontal plane clips everything above it
const UNCLIPPED = 1e4;
// small parts sit inside walls and curtain walls; frame them with their surroundings
const FOCUS_RADIUS = 8;
// around the building rather than of it; hidden until the visitor asks for them
export const CONTEXT = /^(Topography|Site|Planting|Entourage|BuildingPad|Rooms)$/;

const PRESETS: [RegExp, PresetId][] = [
  [/^Roofs$/, 'roof'],
  [/^(Walls|Ceilings)$/, 'plaster'],
  [/^(Floors|BuildingPad)$/, 'floor'],
  [/^Structural|^(Columns|Stairs)$/, 'concrete'],
  [/^(Doors|Windows|Furniture|FurnitureSystems|Casework)$/, 'wood'],
  [/^(CurtainWallMullions|StairsRailing|Railings)$/, 'metal'],
];

export function categoryPreset(category: string): PresetId {
  for (const [re, id] of PRESETS) if (re.test(category)) return id;
  return 'generic';
}

// The station's one Revit layer, while it is mounted: the panel reads element boxes from it.
export const rvtLayer: { rig: LayerRig | null } = { rig: null };

// Revit meshes as one rigged layer, all levels in one group so the file costs a draw call per
// preset rather than per level and preset.
export function buildRvtLayer(
  meshes: MeshData[],
  elements: RvtElement[],
): { rig: LayerRig; root: THREE.Object3D } {
  const source: SourceMesh[] = meshes.map((m) =>
    m.source === 'box' ? { ...m, color: [m.color[0], m.color[1], m.color[2], BOX_ALPHA] } : m,
  );
  const list: SourceElement[] = elements.map((e) => ({
    expressID: e.id,
    type: e.category,
    name: e.name || e.typeName,
    storeyId: null,
    preset: categoryPreset(e.category),
  }));
  const tree = meshSource(source, list, []).object;
  const layer = buildBatches(tree, { keyOf: presetKey });
  const rig = rigLayer(layer, 'revealed', false);
  rig.setClipY(UNCLIPPED);
  return { rig, root: layer.root };
}

export function showContext(rig: LayerRig, show: boolean): void {
  for (const el of rig.elements.values())
    if (CONTEXT.test(el.type)) rig.setElementVisible(el.expressID, show);
}

export function elementFocus(
  expressID: number,
): { center: [number, number, number]; radius: number } | null {
  const el = rvtLayer.rig?.elements.get(expressID);
  if (!el) return null;
  const box = elementBox(el, new THREE.Box3());
  if (box.isEmpty()) return null;
  const c = box.getCenter(new THREE.Vector3());
  return { center: [c.x, c.y, c.z], radius: Math.max(FOCUS_RADIUS, box.getSize(c).length() / 2) };
}
